import Vue from 'vue'
import { keyBy, assign, lowerFirst } from 'lodash'

const initialProfile = () => ({
  personalDetail: {
    firstName: null,
    lastName: null,
    phoneNumber: null,
    initialPhoneNumber: null,
    email: null,
    selectedNewsletter: false,
    hasSubscription: false,
    hasPayment: false,
    sbeCompliance: null,
    uuid: null
  },
  addresses: [],
  shipping: {
    type: 'BillingAndShipping',
    countryCode: null,
    stateCode: null,
    city: null,
    zipCode: null,
    addition: null,
    street: null,
    houseNumber: null,
    extension: null,
    line1: null,
    line2: null,
    county: null
  },
  hasBilling: false,
  billing: {
    type: 'Billing',
    countryCode: null,
    stateCode: null,
    city: null,
    zipCode: null,
    addition: null,
    street: null,
    houseNumber: null,
    extension: null,
    line1: null,
    line2: null,
    county: null
  }
})

const initialPaymentDetails = () => ({
  bankAccountNumber: null,
  bankAccountOwnerName: null,
  bankCountryCode: null,
  createdDate: null,
  expiryDate: null,
  hasPaymentProfile: false,
  id: null,
  isDefault: false,
  lastFourDigits: null,
  nameOnCard: null,
  payerAccount: null,
  paymentMethodCode: null,
  paymentMethodName: null,
  paymentProvider: null,
  sbeCompliance: null,
  status: null,
  type: null
})

const state = () => ({
  token: window.sessionStorage.getItem('Philips-User-Token') || null,
  profile: initialProfile(),
  paymentDetails: initialPaymentDetails(),
  invoiceDetails: {
    allowToPaymentPastDue: false,
    invoices: []
  },
  order: {
    number: null,
    addresses: [],
    hasSubscription: false,
    hasPayment: false,
    isPreviewing: false,
    paymentRequirement: null,
    firstTermTaxAmount: null,
    firstTermTotalAmount: null
  },
  paymentActivity: {
    hasPaymentInProgress: false,
    hasInvalidRecurringPaymentDetail: false,
    hasInitialPaymentInProgress: false
  }
})

function formatCardNumber (paymentMethodName, lastFourDigits) {
  if (paymentMethodName && lastFourDigits) {
    if (lastFourDigits.indexOf('*') !== -1) {
      return paymentMethodName + ' ' + lastFourDigits
    } else {
      return paymentMethodName + ' ****' + lastFourDigits
    }
  } else {
    return ''
  }
}

const getters = {
  isAuthenticated: state => state.token != null,

  paymentActivity: state => state.paymentActivity,

  hasAddress: state => state.profile.addresses && state.profile.addresses.length > 0,

  normalizedPaymentDetails (state) {
    return {
      ...state.paymentDetails,
      cardNumber: formatCardNumber(state.paymentDetails.paymentMethodName, state.paymentDetails.lastFourDigits),
      expiryDate: state.paymentDetails.expiryDate && state.paymentDetails.expiryDate.replace(/\//gi, ' | ')
    }
  },

  billingInvoices: state => state.invoiceDetails.invoices || [],

  openBalanceInvocies: (state, getters) => {
    return getters.billingInvoices && getters.billingInvoices.filter(invocie => !!invocie.hasBalance)
  },

  hasOpenBalance: (state, getters) => {
    return getters.openBalanceInvocies && getters.openBalanceInvocies.length > 0
  },

  isShowPastDueSection: state => state.invoiceDetails.allowToPaymentPastDue
}

const mutations = {
  resetUser (state) {
    assign(state.profile, initialProfile())
    assign(state.paymentDetails, initialPaymentDetails())
    state.invoiceDetails = {}
  },

  setToken (state, token) {
    state.token = token
    window.sessionStorage.setItem('Philips-User-Token', token)
  },

  deleteToken (state) {
    state.token = null
    window.sessionStorage.removeItem('Philips-User-Token')
  },

  setPaymentActivity (state, paymentActivity) {
    if (paymentActivity) {
      state.paymentActivity = paymentActivity
    }
  },

  setUser (state, userDetails) {
    state.profile.personalDetail = {
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      phoneNumber: userDetails.phoneNumber,
      email: userDetails.email,
      selectedNewsletter: userDetails.selectedNewsletter,
      hasSubscription: userDetails.hasSubscription || state.profile.personalDetail.hasSubscription,
      sbeCompliance:userDetails.sbeCompliance,
      hasPayment: userDetails.hasPayment || state.profile.personalDetail.hasPayment,
      uuid: userDetails.uuid || state.profile.personalDetail.uuid,
      hasInvalidRecurringPaymentDetail: userDetails.hasInvalidRecurringPaymentDetail,
      ableToSubscribe: userDetails.ableToSubscribe,
      ableToUpdateAddress: userDetails.ableToUpdateAddress
    }
    state.profile.shipping = assign(state.profile.shipping, userDetails.shipping)
    state.profile.billing = assign(state.profile.billing, userDetails.billing)
    state.profile.hasBilling = userDetails.hasBilling || state.profile.hasBilling
    state.profile.addresses = userDetails.addresses || state.profile.addresses
    window.sessionStorage.setItem('personalDetail', JSON.stringify(state.profile.personalDetail))
    window.sessionStorage.setItem('profile', JSON.stringify(state.profile.shipping))
  },

  setShippingAddressItem (state, { key, value }) {
    Vue.set(state.profile.shipping, key, value)
  },

  setBillingAddressItem (state, { key, value }) {
    Vue.set(state.profile.billing, key, value)
  },

  setHasBilling (state, hasBillingAddress) {
    Vue.set(state.profile, 'hasBilling', hasBillingAddress)
    if (!hasBillingAddress) {
      Vue.set(state.profile.shipping, 'type', 'BillingAndShipping')
    } else {
      Vue.set(state.profile.shipping, 'type', 'Shipping')
    }
  },

  setProfileAddress (state, { shipping, hasBilling, billing }) {
    state.profile.shipping = assign(state.profile.shipping, shipping)
    state.profile.hasBilling = hasBilling
    state.profile.billing = assign(state.profile.billing, billing)
  },

  setPersonalDetailItem (state, { key, value }) {
    Vue.set(state.profile.personalDetail, key, value)
  },

  setPersonalDetail (state, personalDetail) {
    state.profile.personalDetail = personalDetail
  },

  setPaymentDetails (state, paymentDetails) {
    state.paymentDetails = paymentDetails
  },

  setBillingInvoices (state, invoiceDetails) {
    state.invoiceDetails = invoiceDetails
  },

  setInitialPhoneNumber (state, phoneNumber) {
    state.profile.personalDetail.initialPhoneNumber = phoneNumber
  },

  setAddressArray (state, addresses) {
    state.profile.addresses = addresses
  },

  setsbeCompliance (state, sbeCompliance) {
    state.profile.personalDetail.sbeCompliance = sbeCompliance
  },

  deleteOrder (state) {
    state.order = {
      number: null,
      addresses: [],
      hasSubscription: false,
      paymentRequirement: null,
      isPreviewing: false,
      hasPayment: false,
      firstTermTaxAmount: null,
      firstTermTotalAmount: null
    }
    window.localStorage.removeItem('orderNumber')
  },
  resetProfile (state) {
    const defaultProfileState = initialProfile()
    assign(state.profile, defaultProfileState)
  }
}

const actions = {
  loadUserInfoFromLocalStorage ({ commit, dispatch }) {
    let personalDetail = JSON.parse(window.sessionStorage.getItem('personalDetail'))
    if (personalDetail) {
      commit('setPersonalDetail', personalDetail)
    } else {
      dispatch('logout')
    }
  },

  reset ({ commit }) {
    commit('resetUser')
    commit('deleteOrder')
    commit('resetProfile')
    window.localStorage.removeItem('personalDetail')
    window.localStorage.removeItem('loginRole')
    window.localStorage.removeItem('packingInfo')
  },

  async loginUdi ({ commit }, { accessToken }) {
    const userProfile = await Vue.api.account.loginUdi({ accessToken })
    commit('setToken', userProfile.token)
    commit('setUser', userProfile.details)
    return userProfile
  },

  async loginAdmin ({ commit }, { customerEmail, adminUserName, adminPassword }) {
    const userProfile = await Vue.api.admin.signIn({ customerEmail, adminUserName, adminPassword })
    commit('setToken', userProfile.token)
    commit('setUser', userProfile.details)
    return userProfile
  },

  async logout ({ commit, dispatch, state }) {
    commit('deleteToken')
    window.localStorage.removeItem('bundlesInfor')
    window.localStorage.removeItem('storedCartItems')
    window.localStorage.removeItem('bundlesWithPromotions')
    window.localStorage.removeItem('subscriptionInfo')
    window.localStorage.removeItem('currentEstimatedZipCode')
    dispatch('reset')
  },

  async fetchProfile ({ commit }) {
    const profile = await Vue.api.account.fetchProfile()
    let normalizedAddresses = keyBy(profile.addresses, function (o) {
      if (o.type === 'BillingAndShipping') return 'shipping'
      else return lowerFirst(o.type)
    })
    let hasBilling = Boolean(normalizedAddresses.billing)
    let normalizedProfile = assign(profile, normalizedAddresses, { hasBilling })
    commit('setUser', normalizedProfile)
    commit('setInitialPhoneNumber', profile.phoneNumber)
    return profile
  },

  async fetchMyPaymentMethod ({ commit }) {
    const paymentDetails = await Vue.api.account.getMyPaymentMethod()
    commit('setPaymentDetails', paymentDetails)
  },

  async fetchBillingInvoices ({ commit }) {
    const invoiceDetails = await Vue.api.billing.getInvoiceAll()
    commit('setBillingInvoices', invoiceDetails)
  },

  async fetchPaymentActivity ({ commit }) {
    const paymentActivity = await Vue.api.account.getPaymentActivity()
    commit('setPaymentActivity', paymentActivity)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
